
import React from 'react';
import TopMetrics from '@/components/TopMetrics';
import GoogleAdsMetrics from '@/components/GoogleAdsMetrics';
import LeadsTable from '@/components/LeadsTable';
import LeadsAnalytics from '@/components/leads/LeadsAnalytics';
import DailySummary from '@/components/DailySummary';
import { useToast } from "@/components/ui/use-toast";
import { Lead } from '@/types/leads';
import { Funnel } from '@/types/funnels';
import { GoogleAd } from '@/types/google-ads';
import { supabase } from "@/integrations/supabase/client";
import Papa from 'papaparse';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Database } from '@/integrations/supabase/types';
import BrandingButton from '@/components/BrandingButton';

type DbLead = Database['public']['Tables']['leads']['Row'] & {
  status: 'won' | 'lost' | 'in_progress' | null;
};

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterValue, setFilterValue] = React.useState("");
  const [sortConfig, setSortConfig] = React.useState<{
    key: string;
    direction: 'ascending' | 'descending';
  }>({
    key: 'customer',
    direction: 'ascending'
  });
  const [selectedLeads, setSelectedLeads] = React.useState<Lead[]>([]);
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date());
  const [currentWonLeadIndex, setCurrentWonLeadIndex] = React.useState(0);

  const { data: leads = [], isLoading: isLoadingLeads } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching leads:', error);
        toast({
          title: "Error",
          description: "Failed to fetch leads",
          variant: "destructive",
        });
        throw error;
      }

      const transformedData = (data || []).map((lead: DbLead) => ({
        ...lead,
        status: lead.status || 'in_progress' as const
      })) as Lead[];

      return transformedData;
    },
  });

  const totalLeadValue = React.useMemo(() => {
    return leads.reduce((total, lead) => {
      const value = lead.lead_value ? parseFloat(lead.lead_value) : 0;
      return total + value;
    }, 0);
  }, [leads]);

  const filteredLeads = React.useMemo(() => {
    let sortedLeads = leads.filter(lead => {
      const searchStr = filterValue.toLowerCase();
      return (
        lead.customer?.toLowerCase().includes(searchStr) ||
        lead.source?.toLowerCase().includes(searchStr) ||
        lead.lead_quality?.toLowerCase().includes(searchStr) ||
        lead.sales_person_assigned?.toLowerCase().includes(searchStr) ||
        lead.sales_funnel_source?.toLowerCase().includes(searchStr)
      );
    });

    if (sortConfig) {
      sortedLeads.sort((a, b) => {
        if (sortConfig.key === 'customer') {
          const aValue = (a.customer || '').toLowerCase();
          const bValue = (b.customer || '').toLowerCase();
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });
    }

    return sortedLeads;
  }, [leads, filterValue, sortConfig]);

  const { data: funnels = [], isLoading: isLoadingFunnels } = useQuery({
    queryKey: ['funnels'],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      console.log('Fetching funnels...');
      const { data, error } = await supabase
        .from('funnels')
        .select('*')
        .order('last_updated', { ascending: false });
      
      if (error) {
        console.error('Error fetching funnels:', error);
        toast({
          title: "Error",
          description: "Failed to fetch funnels",
          variant: "destructive",
        });
        throw error;
      }

      console.log('Fetched funnels:', data);
      return data || [];
    },
  });

  const getWonLeads = React.useCallback(() => {
    const wonLeads = leads.filter(lead => lead.status === 'won');
    if (wonLeads.length === 0) return [];
    
    return [...wonLeads].sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
      const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [leads]);

  const wonLeads = React.useMemo(() => getWonLeads(), [getWonLeads]);

  React.useEffect(() => {
    if (wonLeads.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentWonLeadIndex(current => {
        console.log('Current index:', current, 'Total won leads:', wonLeads.length);
        return current === wonLeads.length - 1 ? 0 : current + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [wonLeads]);

  React.useEffect(() => {
    setCurrentWonLeadIndex(0);
  }, [wonLeads]);

  const { data: ads = [], isLoading: isLoadingAds } = useQuery({
    queryKey: ['google_ads'],
    queryFn: async () => {
      try {
        const result = await supabase
          .from('google_ads')
          .select('*')
          .order('last_updated', { ascending: false });
        
        if (result.error) {
          throw result.error;
        }

        // Explicitly cast the data to match our GoogleAd type
        return (result.data || []).map(ad => ({
          id: ad.id,
          ad_name: ad.ad_name,
          ad_link: ad.ad_link,
          assets_link: ad.assets_link,
          split_test: ad.split_test || 'false',
          runtime: ad.runtime,
          leads: ad.leads || 0,
          last_updated: ad.last_updated,
          created_by: ad.created_by
        })) as GoogleAd[];
      } catch (error: any) {
        console.error('Error fetching ads:', error);
        toast({
          title: "Error",
          description: "Failed to fetch Google Ads",
          variant: "destructive",
        });
        throw error;
      }
    },
  });

  const handleAddAd = async (newAd: Omit<GoogleAd, 'id' | 'last_updated' | 'leads' | 'created_by'>) => {
    try {
      const result = await supabase
        .from('google_ads')
        .insert([{
          ad_name: newAd.ad_name,
          split_test: newAd.split_test,
          assets_link: newAd.assets_link,
          ad_link: newAd.ad_link,
          runtime: newAd.runtime,
          leads: 0
        }]);
      
      if (result.error) {
        throw result.error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['google_ads'] });
      
      toast({
        title: "Success",
        description: "New Google Ad added successfully",
      });
    } catch (error: any) {
      console.error('Add ad error:', error);
      toast({
        title: "Error",
        description: "Failed to add Google Ad",
        variant: "destructive",
      });
    }
  };

  const handleUpdateAd = async (id: string, field: string, value: string | number) => {
    try {
      const result = await supabase
        .from('google_ads')
        .update({ [field]: value })
        .eq('id', id);
      
      if (result.error) {
        throw result.error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['google_ads'] });
      
      toast({
        title: "Updated",
        description: `Successfully updated ${field}`,
      });
    } catch (error: any) {
      console.error('Update ad error:', error);
      toast({
        title: "Error",
        description: "Failed to update Google Ad",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAd = async (id: string) => {
    try {
      const result = await supabase
        .from('google_ads')
        .delete()
        .eq('id', id);
      
      if (result.error) {
        throw result.error;
      }
      
      await queryClient.invalidateQueries({ queryKey: ['google_ads'] });
      
      toast({
        title: "Success",
        description: "Google Ad deleted successfully",
      });
    } catch (error: any) {
      console.error('Delete ad error:', error);
      toast({
        title: "Error",
        description: "Failed to delete Google Ad",
        variant: "destructive",
      });
    }
  };

  if (isLoadingLeads || isLoadingFunnels || isLoadingAds) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive",
      });
    }
  };

  const handleExport = () => {
    if (selectedLeads.length === 0) {
      toast({
        title: "No leads selected",
        description: "Please select at least one lead to export",
        variant: "destructive",
      });
      return;
    }

    const csv = Papa.unparse(selectedLeads);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'leads_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Success",
      description: `${selectedLeads.length} leads exported successfully`,
    });
  };

  const handleBulkAction = async (action: string) => {
    if (selectedLeads.length === 0) {
      toast({
        title: "No leads selected",
        description: "Please select at least one lead to perform this action",
        variant: "destructive",
      });
      return;
    }

    if (action === 'delete') {
      try {
        const { error } = await supabase
          .from('leads')
          .delete()
          .in('id', selectedLeads.map(lead => lead.id));
        
        if (error) throw error;
        
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        setSelectedLeads([]);
        
        toast({
          title: "Success",
          description: `Deleted ${selectedLeads.length} leads`,
        });
      } catch (error: any) {
        console.error('Bulk delete error:', error);
        toast({
          title: "Error",
          description: "Failed to delete leads",
          variant: "destructive",
        });
      }
    }
  };

  const handleAddFunnel = async (newFunnel: Omit<Funnel, 'id' | 'last_updated' | 'leads' | 'created_by'>) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      const { error } = await supabase
        .from('funnels')
        .insert({
          funnel_name: newFunnel.funnel_name,
          split_test: newFunnel.split_test,
          assets_link: newFunnel.assets_link,
          funnel_link: newFunnel.funnel_link,
          runtime: newFunnel.runtime,
          leads: 0,
          last_updated: new Date().toISOString()
        });
      
      if (error) {
        console.error('Add funnel error:', error);
        throw error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['funnels'] });
      
      toast({
        title: "Success",
        description: "New funnel added successfully",
      });
    } catch (error: any) {
      console.error('Add funnel error:', error);
      toast({
        title: "Error",
        description: "Failed to add funnel",
        variant: "destructive",
      });
    }
  };

  const handleUpdateFunnel = async (id: string, field: string, value: string | number) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      const { error } = await supabase
        .from('funnels')
        .update({
          [field]: value,
          last_updated: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        console.error('Update funnel error:', error);
        throw error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['funnels'] });
      
      toast({
        title: "Updated",
        description: `Successfully updated ${field}`,
      });
    } catch (error: any) {
      console.error('Update funnel error:', error);
      toast({
        title: "Error",
        description: "Failed to update funnel",
        variant: "destructive",
      });
    }
  };

  const handleDeleteFunnel = async (id: string) => {
    try {
      console.log('Deleting funnel with ID:', id);
      const { error } = await supabase
        .from('funnels')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Delete funnel error:', error);
        toast({
          title: "Error",
          description: "Failed to delete funnel",
          variant: "destructive",
        });
        return;
      }
      
      await queryClient.invalidateQueries({ queryKey: ['funnels'] });
      
      toast({
        title: "Success",
        description: "Funnel deleted successfully",
      });
    } catch (error) {
      console.error('Delete funnel error:', error);
      toast({
        title: "Error",
        description: "Failed to delete funnel",
        variant: "destructive",
      });
    }
  };

  const handleAddLead = async (newLead: Omit<Lead, 'id' | 'created_at' | 'created_by'>) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      console.log('Adding new lead:', newLead);
      
      const { data: leadData, error: leadError } = await supabase
        .from('leads')
        .insert({
          ...newLead,
          created_at: new Date().toISOString(),
          status: newLead.status || 'in_progress'
        })
        .select()
        .single();
      
      if (leadError) {
        console.error('Supabase error:', leadError);
        throw leadError;
      }

      if (newLead.sales_funnel_source) {
        await updateFunnelLeadCount(newLead.sales_funnel_source);
      }
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['funnels'] })
      ]);
      
      toast({
        title: "Success",
        description: "New lead added successfully",
      });
    } catch (error: any) {
      console.error('Error adding lead:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add lead",
        variant: "destructive",
      });
    }
  };

  const handleUpdateLead = async (id: string, updatedLead: Partial<Lead>) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      const { data: currentLead } = await supabase
        .from('leads')
        .select('sales_funnel_source')
        .eq('id', id)
        .single();

      console.log('Updating lead with ID:', id, 'Data:', updatedLead);
      
      const { error } = await supabase
        .from('leads')
        .update({
          ...updatedLead,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (updatedLead.sales_funnel_source || currentLead?.sales_funnel_source) {
        if (currentLead?.sales_funnel_source) {
          await updateFunnelLeadCount(currentLead.sales_funnel_source);
        }
        if (updatedLead.sales_funnel_source && updatedLead.sales_funnel_source !== currentLead?.sales_funnel_source) {
          await updateFunnelLeadCount(updatedLead.sales_funnel_source);
        }
      }
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['funnels'] })
      ]);
      
      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
    } catch (error: any) {
      console.error('Error updating lead:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update lead",
        variant: "destructive",
      });
    }
  };

  const handleDeleteLead = async (id: string) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No session found');
      }

      const { data: lead } = await supabase
        .from('leads')
        .select('sales_funnel_source')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('leads')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Delete lead error:', error);
        throw error;
      }

      if (lead?.sales_funnel_source) {
        await updateFunnelLeadCount(lead.sales_funnel_source);
      }
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['funnels'] })
      ]);
      
      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
    } catch (error: any) {
      console.error('Delete lead error:', error);
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive",
      });
    }
  };

  const updateFunnelLeadCount = async (funnelName: string) => {
    try {
      const { data: leadCount, error: countError } = await supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .eq('sales_funnel_source', funnelName);
        
      if (countError) {
        throw countError;
      }

      const { error: updateError } = await supabase
        .from('funnels')
        .update({ 
          leads: leadCount?.length || 0,
          last_updated: new Date().toISOString()
        })
        .eq('funnel_name', funnelName);

      if (updateError) {
        throw updateError;
      }

      queryClient.invalidateQueries({ queryKey: ['funnels'] });
    } catch (error) {
      console.error('Error updating funnel lead count:', error);
      toast({
        title: "Error",
        description: "Failed to update funnel lead count",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen w-full bg-background">
      <header className="w-full bg-[#0f0f0f] text-white">
        <div className="container mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
            <div className="flex items-center">
              <img 
                src="/lovable-uploads/28b4e4f3-8958-4f18-b37c-a791a3196bdc.png" 
                alt="News Anchored" 
                className="h-12 w-auto mb-2 sm:mb-0"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex flex-col items-center sm:items-end">
                <p className="text-xs font-medium text-white">An App By Blacktide</p>
                <p className="text-xs text-white/75">
                  Last updated: {format(lastUpdated, 'PPpp')}
                </p>
                <p className="text-sm font-semibold text-white mt-1">
                  Total Lead Value: ${totalLeadValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSignOut}
                className="text-white hover:text-white/90 hover:bg-white/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {wonLeads.length > 0 && (
        <div className="bg-green-500 text-white p-4 relative overflow-hidden">
          <div 
            className="container mx-auto text-center transition-all duration-500"
            key={wonLeads[currentWonLeadIndex].id}
          >
            <p 
              className="text-sm font-medium"
              style={{
                animation: 'fade-in 0.5s ease-out'
              }}
            >
              🎉 {wonLeads[currentWonLeadIndex].sales_person_assigned || "Someone"} won a lead for {wonLeads[currentWonLeadIndex].customer}
              {wonLeads[currentWonLeadIndex].lead_value && ` valued at $${parseFloat(wonLeads[currentWonLeadIndex].lead_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}!
            </p>
          </div>
        </div>
      )}

      <main className="container mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <TopMetrics 
          funnels={funnels} 
          onAddFunnel={handleAddFunnel}
          onUpdateFunnel={handleUpdateFunnel}
          onDeleteFunnel={handleDeleteFunnel}
        />

        <GoogleAdsMetrics 
          ads={ads}
          onAddAd={handleAddAd}
          onUpdateAd={handleUpdateAd}
          onDeleteAd={handleDeleteAd}
        />

        <div className="space-y-2 mb-6">
          <div className="flex items-baseline gap-3">
            <h2 className="text-xl font-semibold text-gray-800">Active Leads</h2>
            <span className="text-sm text-gray-600">
              ({filteredLeads.length} {filteredLeads.length === 1 ? 'lead' : 'leads'})
            </span>
          </div>
        </div>

        <LeadsTable 
          leads={leads} 
          onAddLead={handleAddLead}
          onUpdateLead={handleUpdateLead}
          onDeleteLead={handleDeleteLead}
          filterValue={filterValue}
          onFilterChange={setFilterValue}
          sortConfig={sortConfig}
          onSort={setSortConfig}
          selectedLeads={selectedLeads}
          onSelectLeads={setSelectedLeads}
          onExport={handleExport}
          onBulkAction={handleBulkAction}
        />

        <div className="mt-8">
          <DailySummary 
            leads={leads} 
            funnels={funnels}
            lastUpdated={lastUpdated}
          />
        </div>

        <div className="mt-8">
          <LeadsAnalytics leads={leads} />
        </div>
      </main>

      <BrandingButton />
    </div>
  );
};

export default Dashboard;
